import { ConfigProvider, Table } from 'antd'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { API } from '@/api'
import { ServerDomainInfo } from '@/api/type-server.ts'
import { ButtonRadio, EmptyView, Loading, Pagination, Radio, Trans } from '@/components'
import { Dropdown } from '@/components/base/Dropdown.tsx'
import { GhostType, MachineType, PaymentType } from '@/consts/machine'
import { Image } from '@/models/image.ts'
import { Machine } from '@/models/machine'
import { Snapshot } from '@/models/snapshot.ts'
import { ImageSelector } from '@/pages/create-instance/components/image-selector.tsx'
import { createColumns } from '@/pages/create-instance/components/machine-table-columns.tsx'
import { SnapshotSelector } from '@/pages/create-instance/components/snapshot-selector.tsx'
import { useUserStore } from '@/stores/user'
import { cn } from '@/utils/style'

import { AcceleratorTypeSelector } from './AcceleratorTypeSelector'
import { WorkloadTypeSelector } from './WorkloadTypeSelector'

const HIDE_DOMAINS = (import.meta.env.VITE_HIDE_DOMAINS ?? '')
  .split(',')
  .map((s: string) => parseInt(s, 10))
  .filter((n: number) => !Number.isNaN(n))

const GPU_MACHINE_SPECS = {
  inference: ['NVIDIAGeForceRTX5090', 'NVIDIAGeForceRTX4090'],
  training: ['NVIDIAH100', 'NVIDIAH200', 'NVIDIAL40', 'NVIDIAA100'],
  render: ['NVIDIARTX6000Ada', 'NVIDIAGeForceRTX4090'],
}

const SPECIAL_CLIENT_MACHINE_SPECS = [
  {
    userId: 2010,
    inference: [],
    training: [
      'NVIDIAH100SXM5-81559MB*1_INTEL(R)XEON(R)PLATINUM8558*16_RAM229376MB_DISK1000GB_PRICE3800000MC',
    ],
    render: [],
    cpu: [],
  },
  {
    userId: 1568, // frank
    inference: [],
    training: [
      'NVIDIAH100SXM5-81559MB*1_INTEL(R)XEON(R)PLATINUM8558*16_RAM229376MB_DISK1000GB_PRICE3800000MC',
    ],
    render: [],
    cpu: [],
  },
]

interface MachineSelectorProps {
  type: MachineType
  period: PaymentType
  onTypeChange: (type: MachineType) => void
  onChange: (value: Machine | undefined) => void
  count: number
  checkedImage?: Image
  setCheckedImage: (value?: Image) => void
  checkedSnap?: Snapshot
  setCheckedSnap: (value?: Snapshot) => void
  onMachinesChange?: (value: Machine[]) => void
  onImagesChange?: (value: Image[]) => void
  onSnapshotsChange?: (value: Snapshot[]) => void
  selectedMachine?: Machine
  selectedImage?: Image
  selectedGhostType?: GhostType
  workloadTypes?: (string | number)[]
}

const REGION_GLOBAL = -1
const REGION_DATA_DRIVE = -2
const REGION_MESH_CLUSTERING = -3

export function MachineSelector({
  type,
  period,
  onTypeChange,
  onChange,
  count,
  checkedImage,
  setCheckedSnap,
  checkedSnap,
  setCheckedImage,
  onMachinesChange,
  selectedMachine,
  onImagesChange,
  onSnapshotsChange,
  selectedGhostType,
  workloadTypes: initialWorkloadTypes,
}: MachineSelectorProps) {
  const { t } = useTranslation()
  const userId = useUserStore((state) => state.id)
  const [loading, setLoading] = useState(false)
  const [domainInfos, setDomainInfos] = useState<ServerDomainInfo[]>([])
  const [rawMachineClasses, setRawMachineClasses] = useState<
    { category: MachineType; classes: any[] }[]
  >([])
  const [allMachines, setAllMachines] = useState<Machine[]>([])
  const [filteredMachines, setFilteredMachines] = useState<Machine[]>([])
  const [page] = useState(1)
  const [ghostType, setGhostType] = useState(GhostType.Image)
  const [expandedRowKeys, setExpandedRowKeys] = useState<string[]>([])
  const [regions, setRegions] = useState<number[]>([])
  const [regionFilterCriteria, setRegionFilterCriteria] = useState<number>(REGION_GLOBAL)
  const [isVgpuChecked, setIsVgpuChecked] = useState(false)
  const [workloadTypes, setWorkloadTypes] = useState<(string | number)[]>(
    initialWorkloadTypes ?? [MachineType.TRAINING_GPU],
  )

  const pageSize = 99

  const machineCategory = useMemo(
    () => (type === MachineType.CPU ? MachineType.CPU : MachineType.GPU),
    [type],
  )

  useEffect(() => {
    if (selectedMachine) {
      const key = (selectedMachine as any).rowKey || selectedMachine.key
      setExpandedRowKeys([key])
    }
  }, [selectedMachine])

  useEffect(() => {
    if (selectedGhostType) setGhostType(selectedGhostType)
  }, [selectedGhostType])

  const handleRowClick = (machine: Machine & { rowKey?: string }) => {
    const key = (machine as any).rowKey || machine.key
    const isExpanded = expandedRowKeys.includes(key)
    setExpandedRowKeys(isExpanded ? [] : [key])
    onChange(isExpanded ? undefined : machine)
  }

  const getDomains = useCallback(async () => {
    setLoading(true)
    try {
      const res = await API.getDomainList()
      if (res.code === 0) {
        setDomainInfos(res.domainInfos.filter((info) => !HIDE_DOMAINS.includes(info.domain)))
        setRegions(res.domainInfos.map((info) => info.domain))
      }
    } finally {
      setLoading(false)
    }
  }, [])

  const getMachineList = useCallback(
    async (p: number = page, force: boolean) => {
      if (force) {
        setLoading(true)
      }
      try {
        let apiRegionFilterCriteria = 0
        if (regionFilterCriteria === REGION_GLOBAL) {
          apiRegionFilterCriteria = 0
        } else if (regionFilterCriteria === REGION_DATA_DRIVE) {
          apiRegionFilterCriteria = 1
        } else if (regionFilterCriteria === REGION_MESH_CLUSTERING) {
          apiRegionFilterCriteria = 2
        }

        const machineCategories =
          machineCategory === MachineType.CPU
            ? [MachineType.CPU]
            : [MachineType.VGPU, MachineType.GPU]
        const promises = machineCategories.map((category) =>
          API.getMachineList({
            page: p,
            per_page: pageSize,
            machine_category: category,
            payment_types: period ? [period] : undefined,
            class_merging: true,
            domains: regions.filter((region) => !HIDE_DOMAINS.includes(region)),
            regions: regions.filter((region) => !HIDE_DOMAINS.includes(region)),
            region_filter_criteria: apiRegionFilterCriteria,
          }),
        )

        const results = await Promise.all(promises)
        const newRawMachineClasses: { category: MachineType; classes: any[] }[] = []
        results.forEach((res, index) => {
          if (res.code === 0) {
            newRawMachineClasses.push({
              category: machineCategories[index],
              classes: res.machineClasses || [],
            })
          }
        })
        setRawMachineClasses(newRawMachineClasses)
      } finally {
        setLoading(false)
      }
    },
    [page, period, domainInfos, regions, machineCategory, pageSize, regionFilterCriteria],
  )

  useEffect(() => {
    const newMachines: Machine[] = []
    rawMachineClasses.forEach((raw) => {
      raw.classes.forEach((m) => {
        newMachines.push(new Machine(raw.category, m))
      })
    })
    setAllMachines(newMachines)
  }, [rawMachineClasses, type])

  useEffect(() => {
    let filtered = allMachines.filter((m) => isVgpuChecked || !m.unitSpec.gpuName?.includes('MIG'))

    if (machineCategory === MachineType.CPU) {
      const special = SPECIAL_CLIENT_MACHINE_SPECS.find((s) => s.userId === userId)
      if (special && special.cpu.length > 0) {
        filtered = filtered.filter((m) =>
          special.cpu.some((spec) => m.unitSpec.classTag.includes(spec)),
        )
      }
    } else if (machineCategory === MachineType.GPU) {
      const special = SPECIAL_CLIENT_MACHINE_SPECS.find((s) => s.userId === userId)
      const allowedSpecs: string[] = []

      const sourceSpecs = special || GPU_MACHINE_SPECS

      if (workloadTypes.includes('all') || workloadTypes.length === 0) {
        allowedSpecs.push(...sourceSpecs.inference, ...sourceSpecs.training, ...sourceSpecs.render)
      } else {
        if (workloadTypes.includes(MachineType.INFERENCE_GPU)) {
          allowedSpecs.push(...sourceSpecs.inference)
        }
        if (workloadTypes.includes(MachineType.TRAINING_GPU)) {
          allowedSpecs.push(...sourceSpecs.training)
        }
        if (workloadTypes.includes(MachineType.RENDER_GPU)) {
          allowedSpecs.push(...sourceSpecs.render)
        }
      }

      const uniqueAllowedSpecs = [...new Set(allowedSpecs)]

      if (uniqueAllowedSpecs.length > 0) {
        filtered = filtered.filter((m) =>
          uniqueAllowedSpecs.some((spec) => m.unitSpec.classTag.includes(spec)),
        )
      } else if (!workloadTypes.includes('all') && workloadTypes.length > 0) {
        filtered = []
      }
    }

    const expanded: Machine[] = []
    filtered.forEach((machine) => {
      if (Array.isArray(machine.domains) && machine.domains.length > 1) {
        machine.domains.forEach((domain) => {
          const newMachine = Object.assign(Object.create(Object.getPrototypeOf(machine)), machine)
          newMachine.domains = [domain]
          ;(newMachine as any).rowKey = `${machine.key}-${domain}`
          expanded.push(newMachine)
        })
      } else if (Array.isArray(machine.domains) && machine.domains.length === 1) {
        (machine as any).rowKey = `${machine.key}-${machine.domains[0]}`
        expanded.push(machine)
      }
    })
    setFilteredMachines(expanded)
    onMachinesChange?.(expanded)
  }, [allMachines, isVgpuChecked, machineCategory, workloadTypes, userId, onMachinesChange])

  useEffect(() => {
    getDomains()
  }, [])

  useEffect(() => {
    if (domainInfos.length > 0) {
      getMachineList(1, true)
    }
  }, [domainInfos, machineCategory, period, regions, regionFilterCriteria, getMachineList])

  return (
    <div>
      <div className="mb-4">
        <div className="flex gap-6">
          <AcceleratorTypeSelector
            value={type}
            onChange={(value) => {
              onChange(undefined)
              setExpandedRowKeys([])
              onTypeChange(value)
            }}
            isVgpuChecked={isVgpuChecked}
            onVgpuCheckedChange={setIsVgpuChecked}
          />

          {machineCategory === MachineType.GPU && (
            <WorkloadTypeSelector value={workloadTypes} onChange={setWorkloadTypes} />
          )}

          <div>
            <div className="text-secondary t-sb mb-2">
              <Trans i18nKey="create.regionLabel">Region</Trans>
            </div>
            <div>
              <Dropdown
                rootClassName="t-sb w-60"
                className="h-11"
                options={[
                  {
                    label: t('common.region.groupGlobal'),
                    value: REGION_GLOBAL,
                    className: 't-sm',
                  },
                  {
                    label: t('common.region.groupDataDrive'),
                    value: REGION_DATA_DRIVE,
                    className: 't-sm',
                  },
                  ...domainInfos.map((info, index) => ({
                    label: t(`common.region.domain${info.domain}`),
                    value: info.domain,
                    className: 't-sm',
                    separatorBefore: index === 0,
                  })),
                ]}
                value={regionFilterCriteria}
                onChange={(value) => {
                  const numValue = Number(value)
                  setRegionFilterCriteria(numValue)

                  if (numValue === REGION_GLOBAL) {
                    setRegions(domainInfos.map((info) => info.domain))
                  } else if (
                    numValue === REGION_DATA_DRIVE ||
                    numValue === REGION_MESH_CLUSTERING
                  ) {
                    setRegions([])
                  } else {
                    setRegions([numValue])
                  }
                }}
              />
            </div>
          </div>
        </div>
      </div>

      {loading && (
        <div className="w-full flex items-center justify-center p-8">
          <Loading />
        </div>
      )}

      {!loading && filteredMachines.length === 0 && (
        <EmptyView
          content={
            <span className="t-sm text-secondary">
              <Trans i18nKey="common.emptyViewNoDataMsg">No Data</Trans>
            </span>
          }
        />
      )}

      {!loading && filteredMachines.length > 0 && (
        <ConfigProvider
          theme={{
            components: {
              Table: {
                rowExpandedBg: '#F1FCF8',
              },
            },
          }}
        >
          <Table
            rowKey="rowKey"
            columns={createColumns(Machine.getMachineType(type) === MachineType.GPU, domainInfos)}
            dataSource={filteredMachines}
            pagination={false}
            loading={loading}
            scroll={{ x: 'max-content' }}
            onRow={(machine) => ({
              onClick: () => {
                if (machine.maxAvailUnits === 0) return
                handleRowClick(machine)
              },
            })}
            rowClassName={(record) => {
              const key = (record as any).rowKey || record.key
              const isSelected = expandedRowKeys.includes(key)
              return cn('cursor-pointer', {
                selected: isSelected,
                'ant-table-row-fixed': isSelected,
              })
            }}
            expandable={{
              expandedRowKeys,
              onExpand: (expanded, record) => {
                const key = (record as any).rowKey || record.key
                if (record.maxAvailUnits === 0) return
                setExpandedRowKeys(expanded ? [key] : [])
              },
              expandIcon: ({ expanded, record }) => (
                <Radio checked={expanded} disabled={record.maxAvailUnits === 0} />
              ),
              expandedRowRender: (machine) => (
                <div className="bg-white rounded-2xl p-6">
                  <div className="flex items-center justify-between gap-x-2 mb-4">
                    <ButtonRadio
                      value={ghostType}
                      items={[
                        {
                          label: t('create.typeLabelOfficialImage'),
                          value: GhostType.Image,
                        },
                        {
                          label: t('create.typeLabelSnapshot'),
                          value: GhostType.Snapshot,
                        },
                      ]}
                      onChange={(value) => {
                        setGhostType(value)
                        setCheckedImage(undefined)
                        setCheckedSnap(undefined)
                      }}
                    />
                    <div className="flex items-center justify-center gap-x-2 px-4 py-3 bg-[#F5F7FA] t-sb rounded-[48px]">
                      <span className="text-tertiary">
                        <Trans i18nKey="create.selectedLabel">Selected</Trans>
                      </span>
                      <span className="text-primary">
                        {checkedImage?.resID
                          ? `${checkedImage.resID} - ${checkedImage.alias ?? ''}`
                          : checkedSnap?.resID
                            ? `${checkedSnap.resID} - ${checkedSnap.name ?? ''}`
                            : '-'}
                      </span>
                    </div>
                  </div>

                  {ghostType === GhostType.Image && (
                    <ImageSelector
                      value={checkedImage}
                      machine={machine}
                      type={Machine.getMachineType(type)}
                      count={count}
                      onChange={setCheckedImage}
                      onImagesChange={onImagesChange}
                    />
                  )}

                  {ghostType === GhostType.Snapshot && (
                    <SnapshotSelector
                      value={checkedSnap}
                      machine={machine}
                      type={Machine.getMachineType(type)}
                      onChange={setCheckedSnap}
                      onSnapshotsChange={onSnapshotsChange}
                      domains={regions}
                    />
                  )}
                </div>
              ),
            }}
          />
        </ConfigProvider>
      )}

      {!loading && (
        <div className="flex items-center justify-between mt-4">
          <div className="text-secondary t-sm flex items-center gap-1"></div>
          {filteredMachines.length > 0 && (
            <Pagination
              size={pageSize}
              total={filteredMachines.length}
              current={page}
              onChange={(page) => {
                getMachineList(page, false)
              }}
            />
          )}
        </div>
      )}
    </div>
  )
}
